import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, CreditCard, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { packageAPI, paymentAPI } from "../lib/api";
import UserLayout from "../components/UserLayout";
import { Button } from "../components/ui/button";

const PurchasePage = () => {
    const { user } = useAuth();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(null);

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const response = await packageAPI.getActive();
            setPackages(response.data);
        } catch (error) {
            toast.error("Gagal memuat paket");
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = async (packageId) => {
        setPurchasing(packageId);
        try {
            const redirectUrl = `${window.location.origin}/dashboard?payment=success`;
            const response = await paymentAPI.create(packageId, redirectUrl);
            
            if (response.data.payment_link) {
                window.location.href = response.data.payment_link;
            } else {
                toast.error("Gagal membuat link pembayaran");
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Gagal membuat pembayaran");
        } finally {
            setPurchasing(null);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(price);
    };

    return (
        <UserLayout>
            <div className="space-y-8" data-testid="purchase-page">
                <div className="text-center">
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2">Beli Credits</h1>
                    <p className="text-gray-600">Pilih paket yang sesuai dengan kebutuhan Anda</p>
                    <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-yellow-50 rounded-full">
                        <CreditCard className="w-5 h-5 text-yellow-600" />
                        <span className="text-yellow-700 font-medium">Credits saat ini: {user?.credits || 0}</span>
                    </div>
                </div>

                {loading ? (
                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-80 rounded-2xl bg-gray-100 animate-pulse" />
                        ))}
                    </div>
                ) : packages.length > 0 ? (
                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        {packages.map((pkg, index) => {
                            const isPopular = index === 1; // Middle package as popular
                            return (
                                <motion.div
                                    key={pkg.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`relative bg-white rounded-2xl p-6 border ${
                                        isPopular ? 'border-red-500 shadow-xl shadow-red-500/10' : 'border-gray-200'
                                    }`}
                                    data-testid={`package-${pkg.id}`}
                                >
                                    {isPopular && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                            <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                                                Paling Populer
                                            </span>
                                        </div>
                                    )}
                                    
                                    <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">{pkg.name}</h3>
                                    <p className="text-gray-500 text-sm mb-4">{pkg.description}</p>
                                    
                                    <div className="mb-6">
                                        <span className="text-3xl font-bold text-gray-900">{formatPrice(pkg.price)}</span>
                                    </div>
                                    
                                    <ul className="space-y-3 mb-6">
                                        <li className="flex items-center gap-2 text-gray-600">
                                            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                                            <span className="font-medium">{pkg.credits} Credits</span>
                                        </li>
                                        <li className="flex items-center gap-2 text-gray-600">
                                            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                                            Semua Background
                                        </li>
                                        <li className="flex items-center gap-2 text-gray-600">
                                            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                                            HD Quality
                                        </li>
                                    </ul>
                                    
                                    <Button
                                        onClick={() => handlePurchase(pkg.id)}
                                        disabled={purchasing === pkg.id}
                                        className={`w-full rounded-xl ${
                                            isPopular 
                                                ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600' 
                                                : ''
                                        }`}
                                        variant={isPopular ? 'default' : 'outline'}
                                        data-testid={`buy-${pkg.id}-btn`}
                                    >
                                        {purchasing === pkg.id ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Memproses...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-4 h-4 mr-2" />
                                                Beli Sekarang
                                            </>
                                        )}
                                    </Button>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Belum ada paket tersedia</p>
                    </div>
                )}

                {/* Payment Info */}
                <div className="max-w-2xl mx-auto bg-gray-50 rounded-2xl p-6">
                    <h3 className="font-heading font-semibold text-gray-900 mb-3">Informasi Pembayaran</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                        <li>• Pembayaran diproses melalui Mayar Payment Gateway</li>
                        <li>• Mendukung Transfer Bank, E-Wallet, dan QRIS</li>
                        <li>• Credits akan otomatis ditambahkan setelah pembayaran sukses</li>
                        <li>• Hubungi support jika ada masalah pembayaran</li>
                    </ul>
                </div>
            </div>
        </UserLayout>
    );
};

export default PurchasePage;
